import React, { useEffect } from 'react';

// assets

// services

// props

// components
import { ActionButton, SpinnerSize, Stack, TagPicker } from '@fluentui/react';
import Label from '../../../typography/label';
import Text from '../../../typography/text';
import { useStore } from '../../../../stores/root';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faContainerStorage, faHashtag, faListCheck, faText, faUser } from '@fortawesome/pro-light-svg-icons';
import Tag, { TTagVariant } from '../../tag';
import { IOutcomeResourceShorterProps } from '../../../../props/finance/outcomes';
import GeneralService from '../../../../services/general';
import { IIncomeInvoiceResourceShortProps } from '../../../../props/finance/incomes/invoices/invoice';
import IncomeInvoiceService from '../../../../services/finance/incomes/invoices/invoice';
import OrderService from '../../../../services/orders/order';
import ISubsidiariesResourceShort from '../../../../props/data/subsidiaries';
import { IIncomeInvoicePartResourceShortProps } from '../../../../props/finance/incomes/invoices/part';
import IncomeInvoicePartService from '../../../../services/finance/incomes/invoices/part';
import LoadingComponent from '../../../feedbacks/loading';

type SelectIncomeInvoicePartProps = {
    selected?: IIncomeInvoicePartResourceShortProps;
    options?: IIncomeInvoicePartResourceShortProps[];
    invoiceId: string;
    qs?: string[];
    label?: string;
    hideLabel?: boolean;
    required?: boolean;
    disabled?: boolean;
    error?: string;
    showInvoiceDetails?: boolean;
    hidePaidUnpaid?: boolean;
    loading?: boolean;
    onChange(selected?: IIncomeInvoicePartResourceShortProps): void;
}

const SelectIncomeInvoicePart: React.FC<SelectIncomeInvoicePartProps> = (props: SelectIncomeInvoicePartProps) => {
    const { user } = useStore();
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [error, setError] = React.useState<string | undefined>();
    const [selected, setSelected] = React.useState<IIncomeInvoicePartResourceShortProps | undefined>();
    const [invoice, setInvoice] = React.useState<IIncomeInvoiceResourceShortProps | undefined>(undefined);
    const [options, setOptions] = React.useState<IIncomeInvoicePartResourceShortProps[]>([]);
    const [loaded, setLoaded] = React.useState<boolean>(!props.loading || false);
    const [required, setRequired] = React.useState<boolean>(props.required || false);

    useEffect(() => {
        _onSelect(props.selected);
    }, [props.selected]);

    useEffect(() => {
        setError(props.error);
    }, [props.error]);

    useEffect(() => {
        setLoaded(!props.loading || false);
    }, [props.loading]);

    useEffect(() => {
        setRequired(props.required || false);
    }, [props.required]);

    useEffect(() => {
        init();
    }, [props.invoiceId, props.options, props.disabled]);

    const init = async () => {
        if (!props.disabled) {
            setLoaded(false);
            if (props.options) {
                setOptions([...props.options]);
            } else {
                const _options = (await IncomeInvoicePartService.retrieve(props.invoiceId, `top=all`)).data;
                setOptions([..._options]);
            }
        }

        setLoaded(true);
    }

    const _onSelect = async (_selected?: IIncomeInvoicePartResourceShortProps) => {
        setSelected(props.selected);

        if (props.showInvoiceDetails && _selected) {
            const _invoice = await IncomeInvoiceService.get(_selected.invoiceId);
            setInvoice(_invoice);
        }
    }

    const _onSearchInvoicePart = async (keyword: string) => {
        try {
            return options.map((item) => ({
                key: item.id,
                name: item.invoiceId + "-" + item.key,
                metadata: item
            }));
        } catch (error) {
            return [];
        }
    }

    const renderSelected = (selected: IIncomeInvoicePartResourceShortProps) => {
        return <Stack styles={{ root: { padding: 8 } }} className={'card'} horizontal tokens={{ childrenGap: 20 }} horizontalAlign={'space-between'} verticalAlign='center'>
            <Stack tokens={{ childrenGap: 10 }} styles={{ root: { width: '100%' } }}>
                <Label size={'xsmall'}>#{selected.invoiceId}-{selected.key}</Label>
                <Stack className={'divider'}> </Stack>
                <Stack tokens={{ childrenGap: 3 }}>
                    <Stack horizontal tokens={{ childrenGap: 5 }}>
                        <Stack styles={{ root: { width: 175 } }}><Text size={'small'}>Total amount (incl. PPn & PPh)</Text></Stack>
                        <Text size={'small'}>: Rp. {GeneralService.getNumberWithSeparator(Number(selected.amount))}</Text>
                    </Stack>
                </Stack>
                {props.showInvoiceDetails && invoice ? <>
                    {invoice.order ? <>
                        <Stack className={'divider'}> </Stack>
                        <Stack horizontal tokens={{ childrenGap: 5 }}>
                            <Stack styles={{ root: { width: 25 } }}><FontAwesomeIcon icon={faContainerStorage} /></Stack>
                            <Text size={'xsmall'}>#{OrderService.getOrderNumber(invoice.order.orderNumber)}</Text>
                        </Stack>
                        <Stack horizontal tokens={{ childrenGap: 5 }}>
                            <Stack styles={{ root: { width: 25 } }}><FontAwesomeIcon icon={faUser} /></Stack>
                            <Text size={'xsmall'}>{invoice.order.company?.name} ({invoice.order.customer.name})</Text>
                        </Stack>
                    </> : null}
                </> : null}
                {!props.hidePaidUnpaid ? <Stack tokens={{ childrenGap: 3 }}>
                    <Stack className={'divider'}> </Stack>
                    <Stack horizontal tokens={{ childrenGap: 5 }}>
                        <Stack styles={{ root: { width: 60 } }}><Text size={'xsmall'}>Paid</Text></Stack>
                        <Text size={'xsmall'} className={'color-green'}>: Rp. {GeneralService.getNumberWithSeparator(Number(selected.paid))}</Text>
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 5 }}>
                        <Stack styles={{ root: { width: 60 } }}><Text size={'xsmall'}>Unpaid</Text></Stack>
                        <Text size={'xsmall'} className={'color-red'}>: Rp. {GeneralService.getNumberWithSeparator(Number(selected.unpaid))}</Text>
                    </Stack>
                </Stack> : null}
            </Stack>
            {!props.disabled ? <ActionButton iconProps={{ iconName: "Delete" }} onClick={() => { props.onChange(); }} /> : null}
        </Stack>
    }

    return <Stack>
        {props.hideLabel ? null : <Label style={{ padding: '3px 0px' }} size={'xsmall'} required={required}>{props.label || "Invoice"}</Label>}
        <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign={'center'}>
            {!loaded ? <LoadingComponent label={""} size={SpinnerSize.small} /> : null}
            <Stack grow={1}>
                {!selected ? <>
                    {!loaded || options.length > 0 ? <TagPicker
                        disabled={props.disabled || !loaded}
                        selectedItems={[]}
                        removeButtonAriaLabel='Remove'
                        itemLimit={1}
                        onItemSelected={(item: any) => {
                            const selected = item ? item.metadata : undefined;
                            props.onChange(selected);

                            return null;
                        }}
                        onRenderSuggestionsItem={(props: any) => {
                            const invoicePart: IIncomeInvoicePartResourceShortProps = props.metadata;
                            let statusVariant: TTagVariant = 'active';

                            if (Number(invoicePart.unpaid) <= 0) { statusVariant = 'success'; }
                            else if (Number(invoicePart.unpaid) > 0) { statusVariant = 'error'; }

                            return <Stack tokens={{ childrenGap: 10 }} horizontalAlign={'baseline'} styles={{ root: { width: '100%', padding: 5 } }}>
                                <Stack styles={{ root: { width: '100%' } }} horizontalAlign={'baseline'}>
                                    <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { width: '100%' } }} horizontalAlign={'space-between'} verticalAlign={'center'}>
                                        <Label size={'small'}>#{invoicePart.invoiceId}-{invoicePart.key}</Label>
                                        <Tag text={Number(invoicePart.unpaid) > 0 ? 'Unpaid' : 'Paid'} size={'small'} variant={statusVariant} />
                                    </Stack>
                                    <Text size={'small'}>(Rp. {GeneralService.getNumberWithSeparator(Number(invoicePart.amount))})</Text>
                                </Stack>
                            </Stack>
                        }}
                        onResolveSuggestions={_onSearchInvoicePart}
                        onEmptyResolveSuggestions={() => _onSearchInvoicePart('')}
                    /> : null}
                    {loaded && options.length < 1 ? <Text size={'small'} style={{ fontStyle: 'italic' }}>No invoice part found</Text> : null}
                </> : null}
                {selected ? renderSelected(selected) : null}
            </Stack>
        </Stack>
        {error ? <Text size={'xsmall'} className={'color-red'} style={{ marginTop: 5 }}>{error}</Text> : null}
    </Stack>;
};

export default SelectIncomeInvoicePart;
